import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, money0 } from "@/lib/api";
import { earnings } from "@/lib/earnings";
import SearchBar from "@/components/SearchBar";
import ListingCard from "@/components/ListingCard";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { BadgePercent, Eye, Scale, ArrowRight } from "lucide-react";
import { ReceiptCard, Marquee, StatsBand, DestinationTiles, TrustChips } from "@/components/LandingBits";

const HERO = "https://images.unsplash.com/photo-1659720879268-818dea77efaa?auto=format&fit=crop&w=2000&q=80";

function FeeCalculator() {
  const [amt, setAmt] = useState([1000]);
  const rows = earnings(amt[0], 0);
  const maxFee = Math.max(...rows.map((r) => r.platformFees));
  const bar = (v) => `${Math.max(6, (v / maxFee) * 100)}%`;
  const color = { fairstay: "bg-primary", "airbnb-host-only": "bg-red-300", "airbnb-split": "bg-red-500", vrbo: "bg-blue-400" };
  return (
    <div className="rounded-[2rem] border bg-white p-6 sm:p-10" data-testid="landing-fee-calculator">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Fee calculator</p>
          <h2 className="mt-1 text-2xl font-semibold sm:text-3xl">On a {money0(amt[0])} stay</h2>
        </div>
        <p className="text-sm text-stone-500">Drag to change the rent + cleaning amount</p>
      </div>
      <Slider data-testid="fee-calculator-slider" value={amt} onValueChange={setAmt} min={100} max={5000} step={50} className="mb-10" />
      <div className="space-y-5">
        {rows.map((r) => (
          <div key={r.key} data-testid={`calc-row-${r.key}`}>
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-4 text-sm">
              <span className="font-semibold">{r.name} <span className="font-normal text-stone-500">· {r.note}</span></span>
              <span className="font-mono-num" data-testid={`calc-fee-${r.key}`}>{money0(r.platformFees)} fees</span>
            </div>
            <div className="h-3 rounded-full bg-stone-100"><div className={`h-3 rounded-full transition-all ${color[r.key]}`} style={{ width: bar(r.platformFees) }} /></div>
            <p className="mt-1 text-xs text-stone-500">Guest pays <span className="font-mono-num" data-testid={`calc-guest-${r.key}`}>{money0(r.guestTotal)}</span> · homeowner earns <span className="font-mono-num" data-testid={`calc-host-${r.key}`}>{money0(r.hostEarnings)}</span></p>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-stone-400" data-testid="calc-disclaimer">Estimates before local lodging tax, which is the same on every platform. Typical published fees as of Nov 2026: Airbnb's 15.5% host-only fee (most hosts) or older split fee (3% host + about 14.1% guest), and Vrbo's 12% commission plus a traveler fee that varies (about 10% used). Real fees vary by account and pricing, and hosts on host-only plans often raise prices to cover the fee.</p>
    </div>
  );
}

const PILLAR_TONES = ["bg-emerald-50 border-emerald-200", "bg-orange-50 border-orange-200", "bg-sky-50 border-sky-200"];

const PILLARS = [
  { icon: BadgePercent, t: "No guest service fee", d: "Guests pay the nightly price and the host's cleaning fee. Hosts pay a flat 10%. That's it." },
  { icon: Eye, t: "Hosts see who's coming", d: "Every request shows the guest's profile, past stays, host reviews and complaint history before you accept." },
  { icon: Scale, t: "Every complaint reviewed by a person", d: "Guests and hosts both share photos and statements, and our team decides based on the evidence." },
];

export default function Landing() {
  const { data: listings = [] } = useQuery({ queryKey: ["listings", "featured"], queryFn: () => api.get("/listings").then((r) => r.data) });
  return (
    <div>
      <section className="relative">
        <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
          <div className="grain relative overflow-hidden rounded-[2rem] bg-stone-900">
            <img src={HERO} alt="Glass cabin at dusk" className="absolute inset-0 h-full w-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-r from-stone-950/90 via-stone-950/55 to-stone-950/10" />
            <div className="relative grid items-center gap-10 px-6 pb-36 pt-14 sm:px-12 sm:pt-20 md:pb-40 lg:grid-cols-[1fr_auto]">
              <div className="max-w-2xl">
                <span className="fade-up inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-md" data-testid="hero-fee-ticker">
                  <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
                  0% guest service fee · flat 10% host commission
                </span>
                <h1 className="fade-up mt-6 text-5xl font-extrabold leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl" style={{ animationDelay: "80ms" }}>
                  Same stays.<br /><span className="text-outline">Smaller</span> <span className="text-emerald-300">bill.</span>
                </h1>
                <p className="fade-up mt-6 max-w-lg text-base text-stone-200 md:text-lg" style={{ animationDelay: "160ms" }}>
                  FairStay takes a flat 10% from hosts and nothing from guests. Airbnb's host-only fee is typically 15.5%, and Vrbo charges about 12% plus a guest fee that varies.
                </p>
                <div className="fade-up mt-6" style={{ animationDelay: "220ms" }}><TrustChips /></div>
              </div>
              <div className="hidden lg:block"><ReceiptCard /></div>
            </div>
          </div>
          <div className="fade-up relative z-10 mx-auto -mt-24 max-w-4xl px-2 sm:-mt-12" style={{ animationDelay: "240ms" }}>
            <SearchBar />
          </div>
        </div>
      </section>

      <div className="mt-16"><Marquee /></div>

      <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-secondary">Where to next</p>
          <h2 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">Pick a vibe. Keep the fees.</h2>
        </div>
        <DestinationTiles />
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-secondary">Featured stays</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">Book direct-feeling prices</h2>
          </div>
          <Link to="/search" data-testid="landing-see-all-link" className="group inline-flex items-center gap-1 text-sm font-semibold text-primary">
            See all stays <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-12" data-testid="featured-listings">
          {listings.filter((l) => l.photos?.length).slice(0, 5).map((l, i) => (
            <div key={l.listing_id} className={i === 0 ? "sm:col-span-2 lg:col-span-6 lg:row-span-2" : "lg:col-span-3"}>
              <ListingCard l={l} large={i === 0} />
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Why we're cheaper</p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">One flat fee, shown up front</h2>
        </div>
        <FeeCalculator />
        <div className="mt-12"><StatsBand /></div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {PILLARS.map(({ icon: Icon, t, d }, i) => (
            <div key={t} className={`fade-up rounded-3xl border p-7 transition-transform duration-300 hover:-translate-y-1 hover:-rotate-1 ${PILLAR_TONES[i]}`} style={{ animationDelay: `${i * 90}ms` }}>
              <span className="mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-white shadow-sm"><Icon className="h-6 w-6 text-secondary" /></span>
              <h3 className="mb-2 text-lg font-semibold">{t}</h3>
              <p className="text-sm leading-relaxed text-stone-600">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-20 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grain relative flex flex-col items-start justify-between gap-6 overflow-hidden rounded-[2rem] bg-primary px-8 py-14 text-white md:flex-row md:items-center md:px-14">
          <span className="pointer-events-none absolute right-2 top-0 font-display text-[6rem] font-extrabold leading-none text-white/10 sm:-top-6 sm:text-[11rem]">10%</span>
          <div className="relative">
            <h2 className="text-3xl font-bold sm:text-4xl">Host on FairStay</h2>
            <p className="mt-2 max-w-lg text-teal-50">List for free, see every guest before you accept, and pay one flat 10% when you get booked.</p>
          </div>
          <Button asChild size="lg" variant="secondary" className="relative rounded-full px-8 transition-transform hover:scale-105" data-testid="landing-host-cta">
            <Link to="/host">Start hosting</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
